import { addDays, addMonths, format, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parisDate } from './shared';

export const dayDate = value => new Date(`${value}T12:00:00`);
export const isoDay = value => format(value, 'yyyy-MM-dd');
export const frenchDay = (value, pattern = 'EEEE d MMMM yyyy') => format(dayDate(value), pattern, { locale: fr });
export const currentDay = () => parisDate();
export const shiftPeriod = (value, view, delta) => isoDay(view === 'month' ? addMonths(dayDate(value), delta) : addDays(dayDate(value), delta * 7));
export const calendarPeriod = (anchor, view) => {
  const base = dayDate(anchor);
  const start = view === 'month' ? startOfMonth(base) : startOfWeek(base, { weekStartsOn: 1 });
  const end = view === 'month' ? endOfMonth(base) : addDays(start, 6);
  const days = eachDayOfInterval({ start, end }).map(isoDay);
  const weekStart = startOfWeek(start, { weekStartsOn: 1 });
  const weeks = [];
  for (let cursor = weekStart; cursor <= end; cursor = addDays(cursor, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, n) => isoDay(addDays(cursor, n))));
  }
  return { start: isoDay(start), end: isoDay(end), days, weeks,
    label: view === 'month' ? format(base, 'MMMM yyyy', { locale: fr }) : `Du ${format(start, 'd MMM', { locale: fr })} au ${format(end, 'd MMM yyyy', { locale: fr })}` };
};
export const hoursLabel = minutes => {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, '0')}`;
};
export const WEEKLY_REFERENCE_HOURS = 39;
// Planned-hours control only: sums the per-plan metrics computed server-side with the Calcul salaire break rule (60/120 min per service day).
export const weekTotals = (plans, days, referenceHours) => {
  const reference = (Number(referenceHours) || WEEKLY_REFERENCE_HOURS) * 60;
  const worked = plans.filter(p => p.status === 'prevu' && days.includes(p.date));
  const planned = worked.reduce((sum, p) => sum + (p.planned_minutes || 0), 0);
  const breaks = worked.reduce((sum, p) => sum + (p.break_minutes || 0), 0);
  const net = worked.reduce((sum, p) => sum + (p.net_minutes || 0), 0);
  const delta = Math.round(net - reference);
  const state = delta < 0 ? 'under' : delta === 0 ? 'reached' : 'over';
  const refLabel = `${reference / 60} h`;
  const label = state === 'under' ? `Sous ${refLabel}` : state === 'reached' ? `${refLabel} atteintes` : `Au-dessus de ${refLabel}`;
  return { planned, breaks, net, reference, delta, state, label, days: worked.length, refLabel };
};

export const SHIFT_NAMES = { AM: 'AM · Matin', PM: 'PM · Après-midi / Soir' };
export const shiftName = plan => plan?.status === 'prevu' ? (SHIFT_NAMES[plan.shift_code] || 'Horaires personnalisés') : '';
export const slotsLabel = plan => (plan.slots?.length ? plan.slots : [plan]).map(s => `${s.start_day_offset ? 'lendemain ' : ''}${s.start_time} – ${s.end_time}${s.end_time < s.start_time ? ' (+1 j)' : ''}`).join(' · ');
export const statusLabel = plan => plan?.status === 'repos' ? 'Repos' : plan?.status === 'absent' ? 'Absent' : 'Travail prévu';
export const planLabel = plan => !plan ? 'Ajouter' : plan.status === 'prevu' ? `${plan.shift_code ? `${plan.shift_code} · ` : ''}${slotsLabel(plan)}` : statusLabel(plan);
const slotMinutes = (slots, baseOffsetDays) => slots.map(s => {
  const [sh, sm] = s.start_time.split(':').map(Number); const [eh, em] = s.end_time.split(':').map(Number);
  const start = (baseOffsetDays + (s.start_day_offset || 0)) * 1440 + sh * 60 + sm;
  let end = (baseOffsetDays + (s.start_day_offset || 0)) * 1440 + eh * 60 + em;
  if (end <= start) end += 1440;
  return [start, end];
});
export const overlappingPlans = (slots, day, neighbors = []) => neighbors.filter(other => {
  if (other.status !== 'prevu' || other.date === day) return false;
  const delta = Math.round((dayDate(other.date) - dayDate(day)) / 86400000);
  const mine = slotMinutes(slots.filter(s => s.start_time && s.end_time), 0);
  const theirs = slotMinutes(other.slots?.length ? other.slots : [other], delta);
  return mine.some(([a, b]) => theirs.some(([c, d]) => a < d && c < b));
});